<template>
  <div class="note-enhancing-overlay-wrapper">
    <div
      ref="noteEnhancingOverlay"
      class="note-enhancing-overlay"
      :class="{ 'rotating-overlay': !props.finished, 'pop-animation': props.finished }"
    >
      <div class="inner">
        <span class="text-sm text-text-default font-medium">{{ displayText }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import useTranslate from '@/utilities/composables/useTranslate';

const { translate } = useTranslate();

const props = defineProps<{
  finished: ReturnType<typeof setTimeout>;
  transcriptAvailable: boolean;
}>();

const WORD_UPDATE_RATE = 3000;

const enhancingIntervalId = ref<ReturnType<typeof setInterval> | null>();
const noteEnhancingOverlay = ref();
const currentEnhancedTextIndex = ref(0);

const enhancingTextList = computed(() => {
  const textList = props.transcriptAvailable
    ? [translate('analysingTranscript'), translate('comparingYourNotes')]
    : [translate('readingYourNotes')];

  return textList.concat([translate('enhancingYourNotes')]).map((text) => `${text}...`);
});

const displayText = computed(() => {
  return props.finished ? translate('finished') : enhancingText.value;
});

const enhancingText = ref(enhancingTextList.value[currentEnhancedTextIndex.value]);

const updateEnhancingText = async () => {
  clearEnhancingInterval();

  if (currentEnhancedTextIndex.value === enhancingTextList.value.length - 1) {
    return;
  }

  currentEnhancedTextIndex.value = currentEnhancedTextIndex.value + 1;
  enhancingText.value = enhancingTextList.value[currentEnhancedTextIndex.value];

  setupEnhancingInterval();
};

const setupEnhancingInterval = () => {
  enhancingIntervalId.value = setInterval(updateEnhancingText, WORD_UPDATE_RATE);
};

const clearEnhancingInterval = () => {
  clearInterval(enhancingIntervalId.value!);
  enhancingIntervalId.value = null;
};

onMounted(() => {
  setupEnhancingInterval();
});

onBeforeUnmount(() => {
  clearEnhancingInterval();
});
</script>
<style lang="scss">
.note-enhancing-overlay-wrapper {
  @apply absolute bg-standard-bg rounded-lg bottom-4 w-[70%] max-w-[420px] left-[50%] z-20;
  transform: translateX(-50%);

  .note-enhancing-overlay {
    @apply relative overflow-hidden flex items-center justify-center
     bg-standard-bg border-important-border border border-solid rounded-lg text-center;
    transition: 0.25s all ease-in-out;
    box-shadow: 0px 2px 4px 0px var(--modalBg);

    .inner {
      @apply bg-standard-bg relative z-[1] w-full p-2 m-1 rounded-lg;
    }
  }

  .rotating-overlay::before {
    content: '';
    @apply block h-[500px] w-[40px] absolute z-0 top-[50%] rounded;
    background: linear-gradient(
      90deg,
      rgba(74, 154, 255, 1) 20%,
      rgba(74, 154, 255, 0.8) 40%,
      rgba(74, 154, 255, 0.6) 60%
    );
    transform: translate(0);
    filter: blur(10px);
    animation: rotate 5s linear forwards infinite;
    transform-origin: top center;
  }

  .pop-animation {
    animation: pop 0.5s ease-in-out;
    animation-iteration-count: 1;
  }

  @keyframes pop {
    0% {
      transform: scale(1);
      @apply bg-standard-bg;
    }

    50% {
      transform: scale(0.98);
      @apply bg-highlight-default;
    }

    100% {
      transform: scale(1);
      @apply bg-standard-bg;
    }
  }

  @keyframes rotate {
    from {
      transform: rotate(0);
    }

    to {
      transform: rotate(360deg);
    }
  }
}
</style>
